import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { getTestData, createTestData } from '../../services/apiService';
import ConnectionStatus from '../common/ConnectionStatus';
import FormControl from '../common/FormControl';
import Button from '../common/Button';
import './ConnectionTest.css';

const ConnectionTest: React.FC = () => {
  const [testData, setTestData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [newItem, setNewItem] = useState({ name: '', description: '' });

  const loadTestData = async () => {
    setLoading(true);
    try {
      const data = await getTestData();
      setTestData(data.data || []);
    } catch { }
    finally { setLoading(false); }
  };

  const addTestData = async () => {
    if (!newItem.name || !newItem.description) return;
    setLoading(true);
    try {
      await createTestData(newItem);
      setNewItem({ name: '', description: '' });
      await loadTestData();
    } catch { }
    finally { setLoading(false); }
  };

  useEffect(() => { loadTestData(); }, []);

  return (
    <div className="netbody-card flex flex-col h-full min-h-[500px]">

      {/* Header */}
      <div className="netbody-header">
        <div className="flex items-center gap-2 ml-auto">
          <Button variant="secondary-outline" onClick={loadTestData} disabled={loading} data-testid="BTN_connection_test_load">
            {loading ? 'Loading…' : 'Load Data'}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="netbody-content">
        <div className="scroll-container flex-1 overflow-hidden" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

        {/* Connection Status */}
        <div className="test-section">
          <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '10px' }}>Connection Status</h3>
          <div className="status-panel">
            <ConnectionStatus showDetails={true} autoRefresh={true} refreshInterval={10000} />
          </div>
        </div>

        {/* Data Display */}
        <div className="test-section">
          <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '10px' }}>
            Current Data ({testData.length} items)
          </h3>
          <div className="data-display">
            {testData.length === 0 ? (
              <div className="data-table-empty">
                <p>No data loaded yet. Click "Load Data" to fetch from backend.</p>
              </div>
            ) : (
              <div className="data-list">
                {testData.map((item: any) => (
                  <div key={item.id} className="data-item">
                    <div className="item-header">
                      <h5>{item.name}</h5>
                      <span className="item-id">ID: {item.id}</span>
                    </div>
                    <p className="item-description">{item.description}</p>
                    <div className="item-meta">
                      <span className="created-at">Created: {new Date(item.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Add New Item */}
        <div className="test-section">
          <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '10px' }}>Add New Test Data</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '480px' }}>
            <FormControl.Input
              label="Name"
              value={newItem.name}
              onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
              placeholder="Enter item name"
              data-testid="INP_connection_test_name"
            />
            <FormControl.Textarea
              label="Description"
              value={newItem.description}
              onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
              placeholder="Enter item description"
              rows={3}
              data-testid="INP_connection_test_description"
            />
            <div>
              <Button
                variant="fill"
                icon={Plus}
                onClick={addTestData}
                disabled={loading || !newItem.name || !newItem.description}
                data-testid="BTN_connection_test_add"
              >
                {loading ? 'Creating…' : 'Add Item'}
              </Button>
            </div>
          </div>
        </div>

        {/* Manual Test */}
        <div className="test-section">
          <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '10px' }}>Manual Connection Test</h3>
          <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>You can also test the backend connection manually:</p>
          <div className="code-block"><code>curl -X GET http://localhost:5001/api/health</code></div>
          <div className="code-block"><code>curl -X GET http://localhost:5001/api/test-data</code></div>
        </div>

        </div>
      </div>
    </div>
  );
};

export default ConnectionTest;
